import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Logo } from './Logo';

/**
 * Shared chrome for all public (unauthenticated) pages: landing, pricing,
 * policies, contact. Gives Razorpay's verification bot a consistent nav +
 * footer that's clearly a real website, not an API-only backend.
 */
export const PublicLayout = ({ children }) => {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const navLinks = [
    { to: '/features', label: 'Features' },
    { to: '/pricing',  label: 'Pricing' },
    { to: '/about',    label: 'About' },
    { to: '/contact',  label: 'Contact' },
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F7F7F5' }}>
      {/* Top nav */}
      <header
        className="sticky top-0 z-30 backdrop-blur"
        style={{
          backgroundColor: 'rgba(247, 247, 245, 0.92)',
          borderBottom: '1px solid #E5E5DF',
        }}
      >
        <div className="max-w-[1280px] mx-auto flex items-center justify-between px-4 md:px-8 py-3">
          <Link to="/" className="flex items-center" data-testid="public-nav-brand">
            <Logo size="md" testid="public-nav-logo" />
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-sm font-medium hover:opacity-70 transition-opacity"
                style={{ color: '#1C1917' }}
                data-testid={`public-nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="text-sm font-medium px-4 py-2 rounded-lg"
                style={{ backgroundColor: '#1B4332', color: '#FFFFFF' }}
                data-testid="public-nav-dashboard-btn"
              >
                Dashboard
              </button>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm font-medium"
                  style={{ color: '#1C1917' }}
                  data-testid="public-nav-login"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="text-sm font-medium px-4 py-2 rounded-lg"
                  style={{ backgroundColor: '#1B4332', color: '#FFFFFF' }}
                  data-testid="public-nav-register"
                >
                  Start free trial
                </Link>
              </>
            )}
          </div>

          <button
            className="md:hidden p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            data-testid="public-nav-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t" style={{ borderColor: '#E5E5DF', backgroundColor: '#FFFFFF' }}>
            <div className="px-4 py-3 space-y-2">
              {navLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="block py-2 text-sm font-medium"
                  style={{ color: '#1C1917' }}
                >
                  {l.label}
                </Link>
              ))}
              {user ? (
                <Link to="/dashboard" onClick={() => setOpen(false)} className="block py-2 text-sm font-medium" style={{ color: '#1B4332' }}>
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="block py-2 text-sm font-medium" style={{ color: '#1B4332' }}>
                    Sign in
                  </Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="block py-2 text-sm font-medium" style={{ color: '#1B4332' }}>
                    Start free trial
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      {/* Footer with Razorpay-required links */}
      <footer
        className="mt-16"
        style={{ backgroundColor: '#FFFFFF', borderTop: '1px solid #E5E5DF' }}
        data-testid="public-footer"
      >
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center mb-3">
              <Logo size="sm" testid="footer-logo" />
            </div>
            <p className="text-sm leading-relaxed" style={{ color: '#57534E' }}>
              Indian equities, clearly tracked. FIFO-based P/L, ITR-ready reports, AI contract-note import.
            </p>
          </div>

          <div>
            <p className="text-xs font-medium uppercase mb-3" style={{ color: '#78716C', letterSpacing: '0.08em' }}>
              Product
            </p>
            <ul className="space-y-2 text-sm" style={{ color: '#1C1917' }}>
              <li><Link to="/features" className="hover:opacity-70">Features</Link></li>
              <li><Link to="/pricing"  className="hover:opacity-70">Pricing</Link></li>
              <li><Link to="/register" className="hover:opacity-70">Start free trial</Link></li>
              <li><Link to="/login"    className="hover:opacity-70">Sign in</Link></li>
            </ul>
          </div>

          <div>
            <p className="text-xs font-medium uppercase mb-3" style={{ color: '#78716C', letterSpacing: '0.08em' }}>
              Company
            </p>
            <ul className="space-y-2 text-sm" style={{ color: '#1C1917' }}>
              <li><Link to="/about" className="hover:opacity-70">About</Link></li>
              <li><Link to="/contact" className="hover:opacity-70">Contact</Link></li>
            </ul>
          </div>

          <div>
            <p className="text-xs font-medium uppercase mb-3" style={{ color: '#78716C', letterSpacing: '0.08em' }}>
              Legal
            </p>
            <ul className="space-y-2 text-sm" style={{ color: '#1C1917' }}>
              <li><Link to="/privacy" className="hover:opacity-70">Privacy Policy</Link></li>
              <li><Link to="/terms"   className="hover:opacity-70">Terms of Service</Link></li>
              <li><Link to="/refund"  className="hover:opacity-70">Refund Policy</Link></li>
            </ul>
          </div>
        </div>

        <div
          className="max-w-[1280px] mx-auto px-4 md:px-8 py-5 border-t flex flex-col md:flex-row items-start md:items-center justify-between gap-3"
          style={{ borderColor: '#E5E5DF' }}
        >
          <p className="text-xs" style={{ color: '#78716C' }}>
            © {new Date().getFullYear()} EquiTrack · Solely developed by Harshesh Shah
          </p>
          <p className="text-xs" style={{ color: '#78716C' }}>
            Contact: <a href="mailto:admin@equitrack.in" className="underline">admin@equitrack.in</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
